<script setup lang="ts">
/**
 * Second step of the login (UC-02 step 1, ADR-078), shown when the API answered the e-mail
 * address with `next: "instance"` or could not look it up (`upstream`, 08 §1). With `choices`
 * the API found several instances for the address (F-104): one button each, the user picks one.
 * Without, a field for the address of the own dataCycle instance (ADR-069), checked with the
 * contract before it leaves. Either way the dialog starts the login with that instance.
 */
import { ref, useId } from "vue";
import { InstanceHost } from "@ai-platform/contracts";
import PixButton from "@pixelpoint-pattern-library-v2/base/components/styled/base/primitives/PixButton/PixButton.vue";
import PixTextField from "@pixelpoint-pattern-library-v2/base/components/styled/base/inputs/PixTextField/PixTextField.vue";

const props = defineProps<{
  /** the instances the API offers; empty means "ask for the address" */
  choices: readonly string[];
  /** the address of the first step, shown with the way back to it */
  email: string;
  /** a start is under way; the buttons wait */
  pending: boolean;
  /** the lookup did not answer, so the address is not known to be unassigned (08 §1) */
  lookupFailed?: boolean | undefined;
}>();

const emit = defineEmits<{
  /** the normalised instance host */
  submit: [instanceHost: string];
  /** back to the e-mail address */
  back: [];
}>();

const instanceHost = ref("");
const fieldError = ref("");
const choicesLabelId = useId();

function submit() {
  const parsed = InstanceHost.safeParse(instanceHost.value);
  if (!parsed.success) {
    fieldError.value = "Bitte nur die Adresse eurer Instanz eingeben, etwa kaernten.datacycle.at.";
    return;
  }
  fieldError.value = "";
  emit("submit", parsed.data);
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <div v-if="props.choices.length > 0" role="group" :aria-labelledby="choicesLabelId" class="flex flex-col gap-3">
      <p :id="choicesLabelId" class="text-ui text-text-body m-0">
        Eure Adresse gehört zu mehreren dataCycle-Instanzen. Wählt die, über die ihr euch anmelden wollt.
      </p>
      <ul class="m-0 flex list-none flex-col gap-2 p-0">
        <li v-for="choice in props.choices" :key="choice">
          <PixButton
            outline
            class="text-ui min-h-(--size-button) w-full font-semibold"
            :disabled="props.pending"
            @click="emit('submit', choice)"
          >
            {{ choice }}
          </PixButton>
        </li>
      </ul>
    </div>
    <form v-else class="flex flex-col gap-3" novalidate @submit.prevent="submit">
      <p class="text-ui text-text-body m-0">
        {{
          props.lookupFailed
            ? "Über welche dataCycle-Instanz meldet ihr euch an?"
            : "Wir haben eure E-Mail-Adresse noch keiner Instanz zugeordnet."
        }}
      </p>
      <PixTextField
        v-model="instanceHost"
        type="url"
        label="Adresse eurer dataCycle-Instanz"
        placeholder="kaernten.datacycle.at"
        :error="fieldError"
        required
      />
      <p class="text-meta text-text-soft m-0">
        Die Adresse, unter der ihr dataCycle öffnet. Ihr meldet euch dort mit eurem Konto an und kommt danach hierher
        zurück.
      </p>
      <PixButton type="submit" class="text-ui mt-1 min-h-(--size-button) w-full font-bold" :disabled="props.pending">
        Anmelden
      </PixButton>
    </form>
    <p class="text-meta text-text-soft m-0">
      Anmeldung für <span class="text-text-body font-semibold break-all">{{ props.email }}</span> ·
      <button
        type="button"
        class="rounded-chip text-accent-text focus-visible:outline-focus cursor-pointer font-semibold underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
        :disabled="props.pending"
        @click="emit('back')"
      >
        Andere E-Mail-Adresse
      </button>
    </p>
  </div>
</template>
